import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { LIST_GATEWAYS_RESOURCE } from 'src/features/gateway/api/useListGateways';
import { zoneRulesMessages } from 'src/features/membershipType/utils/messages';
import { ZoneRule } from 'src/features/membershipType/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import {
    useDeleteMembershipZoneRule
} from 'src/features/membership/api/zoneRule/useDeleteMembershipZoneRule';
import {
    LIST_MEMBERSHIP_ZONE_RULES_RESOURCE
} from 'src/features/membership/api/zoneRule/useListMembershipZoneRules';
import { useParking } from 'src/providers/ParkingProvider';

interface DeleteZoneRuleProps<TData> extends DataTableNewEntryProps<TData> {
    selectedZoneRule: ZoneRule;
    children?: React.ReactNode;
}

function DeleteMembershipZoneRuleModal<TData>({ selectedZoneRule, children }: DeleteZoneRuleProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage, locale } = useIntl();
    const { membershipId = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate: deleteRule } = useDeleteMembershipZoneRule({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIP_ZONE_RULES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_GATEWAYS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(zoneRulesMessages.deleteZoneRuleSuccess),
                description: formatMessage(zoneRulesMessages.deleteZoneRuleSuccessDescription, {
                    entry: selectedZoneRule.zone.translations.find((translation) => translation.language === locale)
                        ?.name,
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...zoneRulesMessages.deleteZoneRule },
                subTitle: {
                    ...zoneRulesMessages.deleteZoneRuleSubtitle,
                    values: {
                        entry:
                            selectedZoneRule?.zone?.translations?.find((temp) => temp.language === locale)?.name ?? '-',
                    },
                },
                icon: ICON_MAP.DELETE as IconProp,
                confirmationMessage: {
                    ...zoneRulesMessages.confirmDeleteZoneRule,
                    values: {
                        entry:
                            selectedZoneRule?.zone?.translations?.find((temp) => temp.language === locale)?.name ?? '-',
                    },
                },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: generalMessages.delete,
                callbackFn: () => deleteRule({ parkingId, membershipId, ruleId: selectedZoneRule.id }),
            }}
        >
            {children}
        </ConfirmationModal>
    );
}

export default DeleteMembershipZoneRuleModal;
